'use client';

/**
 * Global error boundary — Profileia (2026-09-15 redesign).
 *
 * Catches errors in the root layout.tsx itself. When active, REPLACES
 * the root layout entirely — MUST render its own <html> and <body>.
 *
 * SPEC-244 (2026-09-15 — Founder directive "página bonita cuando el
 * servidor esté abajo"):
 *   - Branded Profileia design (gradient + logo placeholder)
 *   - Animated pulse for the "loading" state
 *   - Detects 503/500/network errors + shows appropriate message
 *   - Auto-retry every 10s (with countdown)
 *   - "Volver al inicio" + "Reintentar ahora" buttons
 *   - Status indicator (checking if server is back)
 *
 * Self-contained inline styles (no Tailwind dependency — the layout may
 * have crashed during stylesheet injection).
 * Dark mode support via @media (prefers-color-scheme: dark).
 *
 * §6 Anti-Invención: pure UI — no business logic.
 */

import { useEffect, useState, useCallback } from 'react';

type ErrorType = 'server-down' | 'error' | 'timeout';

function detectErrorType(error: Error & { digest?: string; statusCode?: number }): ErrorType {
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('fetch') || msg.includes('network') || msg.includes('econnrefused') || error.statusCode === 503) {
    return 'server-down';
  }
  if (msg.includes('timeout') || msg.includes('aborted')) {
    return 'timeout';
  }
  return 'error';
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(10);
  const [checking, setChecking] = useState(false);
  const errorType = detectErrorType(error);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') console.error('global-error boundary caught:', error);
  }, [error]);

  // Auto-retry countdown (every 10s)
  useEffect(() => {
    if (retryCount === 0) return; // don't auto-retry on first load
    if (countdown <= 0) {
      setChecking(true);
      // Try to fetch the homepage — if it responds, the server is back
      fetch('/', { method: 'HEAD', cache: 'no-store' })
        .then(() => {
          reset(); // server is back — re-render
        })
        .catch(() => {
          setChecking(false);
          setCountdown(10); // reset countdown for next attempt
        });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [retryCount, countdown, reset]);

  const handleRetry = useCallback(() => {
    setRetryCount((c) => c + 1);
    setCountdown(10);
    setChecking(false);
    reset();
  }, [reset]);

  // Detect dark mode
  const isDark =
    typeof window !== 'undefined' &&
    (document.documentElement.classList.contains('dark') ||
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const colors = isDark
    ? {
        bg: '#0a0f1e',
        bgCard: '#131b2e',
        border: '#1e293b',
        text: '#f1f5f9',
        muted: '#94a3b8',
        faint: '#64748b',
        accent: '#3b82f6',
        accent2: '#06b6d4',
      }
    : {
        bg: '#f8fafc',
        bgCard: '#ffffff',
        border: '#e2e8f0',
        text: '#0f172a',
        muted: '#475569',
        faint: '#94a3b8',
        accent: '#2563eb',
        accent2: '#06b6d4',
      };

  const messages: Record<ErrorType, { title: string; subtitle: string; icon: string }> = {
    'server-down': {
      title: 'Estamos de mantenimiento',
      subtitle: 'Nuestros servidores están reiniciándose. Volveremos en breve.',
      icon: '🔧',
    },
    'timeout': {
      title: 'La conexión tardó demasiado',
      subtitle: 'La solicitud superó el tiempo límite. Intenta de nuevo.',
      icon: '⏱️',
    },
    'error': {
      title: 'Algo salió mal',
      subtitle: 'Se produjo un error inesperado. Puedes intentar de nuevo.',
      icon: '⚠️',
    },
  };

  const msg = messages[errorType];

  return (
    <html lang="es" className={isDark ? 'dark' : ''}>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{msg.title} — Profileia</title>
      </head>
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          background: `radial-gradient(ellipse at top, ${colors.bg} 0%, ${isDark ? '#050810' : '#f1f5f9'} 100%)`,
          color: colors.text,
          padding: '1.5rem',
        }}
      >
        <div
          style={{
            maxWidth: '28rem',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {/* Logo placeholder — Profileia gradient circle */}
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accent2} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.75rem',
              boxShadow: `0 0 40px ${colors.accent}40`,
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            <span style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}>🚀</span>
          </div>

          {/* Error icon */}
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{msg.icon}</div>

          {/* Title */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', letterSpacing: '-0.025em' }}>
            {msg.title}
          </h1>

          {/* Subtitle */}
          <p style={{ color: colors.muted, marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '0.95rem' }}>
            {msg.subtitle}
          </p>

          {/* Auto-retry countdown (only for server-down) */}
          {errorType === 'server-down' && retryCount > 0 && !checking && countdown > 0 && (
            <p style={{ color: colors.faint, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Reintentando en {countdown}s…
            </p>
          )}
          {checking && (
            <p style={{ color: colors.faint, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Verificando conexión…
            </p>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accent2} 100%)`,
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.625rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.15s ease',
              }}
            >
              Reintentar ahora
            </button>
            <a
              href="/"
              style={{
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                padding: '0.625rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Volver al inicio
            </a>
          </div>

          {/* Error digest (for support) */}
          {error?.digest && (
            <p style={{ marginTop: '2rem', fontSize: '0.75rem', color: colors.faint }}>
              Código: <code style={{ fontFamily: 'monospace' }}>{error.digest}</code>
            </p>
          )}

          {/* Footer */}
          <div style={{ marginTop: '2.5rem', fontSize: '0.8rem', color: colors.faint }}>
            <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Profileia</p>
            <p>La casa del emprendedor</p>
          </div>
        </div>

        {/* CSS animations */}
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.9; }
          }
        `}</style>
      </body>
    </html>
  );
}
